<template>
  <el-container class="screenHeight">
    <el-header class="level">
      <span class="level-left">
        <el-page-header @back="handleBack">
          <template #title>
            {{ $t('components.embossing-footer.backButton') }}
          </template>

          <template #content>
            {{ $t(`enum.template-progress.${activeTemplateProgress}`) }}
          </template>
        </el-page-header>
      </span>
      <span class="level-right">
        <!--<el-button
          link
          class="level-item"
          @click="saveUpdated"
          v-loading.fullscreen.lock="fullscreenLoading"
          :disabled="!hasChanges"
        >
          <font-awesome-icon icon="save" />
        </el-button>-->
        <span class="level-item">
          {{ authentication.user?.firstname }}
          {{ authentication.user?.lastname }}
        </span>
      </span>
    </el-header>

    <el-container>
      <el-aside v-if="showTree" class="withFooterAndHeader" width="20rem">
        <el-scrollbar>
          <MeshTree
            v-model="meshTreeData"
            :add-url="templateId ? `/template-upload?templateId=${templateId}` : `/template-upload`"
            :can-calculate-result="true"
            @delete-item="deleteItem"
          ></MeshTree>
        </el-scrollbar>
      </el-aside>
      <ToggleSidebar v-model="showTree" />
      <el-main class="flex-grow-1 main-content" style="height: 93vh; overflow: hidden; position: relative;">
        <MeshEditor
          ref="meshEditor"
          v-model="meshTreeData"
          :fit-camera-to-scene="true"
          :activate-aline-to-floor="true"
          :can-curve="activeTemplateProgress === TemplateProgress.curves"
          :can-deform="activeTemplateProgress === TemplateProgress.modifier"
          :can-transform="activeTemplateProgress === TemplateProgress.setup"
          :can-mirror="activeTemplateProgress === TemplateProgress.setup"
          :can-copy="activeTemplateProgress === TemplateProgress.setup"
          :can-aline="activeTemplateProgress === TemplateProgress.setup"
          :can-group="activeTemplateProgress === TemplateProgress.setup"
          :can-colorize="false"
          :can-embossing="activeTemplateProgress === TemplateProgress.embossing"
          :can-render="activeTemplateProgress === TemplateProgress.render"
          :can-resolution="true"
          :has-toolbar="true"
          :fullscreen="false"
          :show-orientation-gizmo="true"
          :show-siderbar-right-tools="false"
          @product-quality-changed="productQualityChanged"
        ></MeshEditor>
      </el-main>
    </el-container>
    <Workflow class="workflow" :workflow-model="workflowModel" :fullscreen-loading="fullscreenLoading" />
    <create-template v-if="!templateId" />
  </el-container>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';
import * as THREE from 'three';
import { useRoute, useRouter } from 'vue-router';

import MeshTree from '@/components/three/scenes/MeshTree.vue';
import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import OrientationGizmo from '@/components/three/OrientationGizmo.vue';
import MeshEditor from '@/components/three/scenes/MeshEditor.vue';
import { FileType3D, UploadCategory } from '@/types/enum/upload';
import { TemplateProgress } from '@/types/enum/workflow';
import * as LayoutUtility from '@/utils/layout';
import * as TemplateService from '@/services/api/templateService';
import * as modelService from '@/services/api/modelService';
import Workflow from '@/components/workflow/Workflow.vue';
import { WorkflowModel } from '@/types/ui/WorkflowModel';
import ToggleSidebar from '@/components/ui-elements/ToggleSidebar.vue';
import { DefaultUndoRedo } from '@/types/ui/HistoryList';
import { ProductQuality } from '@/types/enum/template';
import Auth from '@/services/api/auth';
import CreateTemplate from '@/components/forms/CreateTemplate.vue';

@Component({
  components: {
    CreateTemplate,
    MeshEditor,
    OrientationGizmo,
    MeshTree,
    Workflow,
    ToggleSidebar,
  },
})
/* eslint-disable @typescript-eslint/no-explicit-any*/

export default class TemplateWorkflow extends Vue implements DefaultUndoRedo {
  @Prop({ default: null }) templateId!: number | null;

  router = useRouter();
  route = useRoute();
  meshTreeData: MeshTreeData = new MeshTreeData(null, this);
  showTree = true;
  lastUpdateDB = -1;
  fullscreenLoading = false;
  meshEditor!: MeshEditor;
  modelLoading = false;

  selectedProductQuality = ProductQuality.low.toString();

  workflowModel: WorkflowModel<TemplateProgress> = new WorkflowModel<TemplateProgress>('template-progress', TemplateProgress);
  TemplateProgress = TemplateProgress;

  authentication = Auth.getInstance();

  get activeTemplateProgress(): TemplateProgress {
    if (this.workflowModel.active < Object.keys(TemplateProgress).length) {
      LayoutUtility.refresh();
      return Object.values(TemplateProgress)[this.workflowModel.active];
    }
    return TemplateProgress.setup;
  }

  set activeTemplateProgress(value: TemplateProgress) {
    this.workflowModel.setStep(Object.values(TemplateProgress).indexOf(value));
  }

  unmounted(): void {
    (this.router as any).askForChanges = false;
  }

  mounted(): void {
    this.$nextTick(() => {
      if (this.$refs.meshEditor) {
        this.meshEditor = this.$refs.meshEditor as MeshEditor;
      }
    });

    this.loadFromDB();
  }

  loadFromDB(): void {
    if (this.templateId) {
      this.modelLoading = true;
      this.meshTreeData.clear();
      TemplateService.getMeshData(this.templateId, ProductQuality[this.selectedProductQuality as keyof typeof ProductQuality]).then((importData) => {
        if (this.templateId) {
          if (importData && importData.length > 0) {
            const loadChildren = (parent: string | MeshTreeDataItem | null, children: modelService.MeshExportData[]): void => {
              for (const data of children) {
                let treeData!: MeshTreeDataItem;
                if (data.fileType !== FileType3D.NONE) {
                  treeData = this.meshTreeData.addBase64Import(
                    data.uniqueKey,
                    data.componentId,
                    data.name,
                    data.fileType,
                    data.base64,
                    data.thumbnailType,
                    data.thumbnail,
                    parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                    false,
                    data.color,
                    data.position,
                    data.rotation,
                    data.scale
                  );
                  treeData.bones = data.bones.map((boneInfo) => {
                    const bone = new THREE.Bone();
                    bone.name = boneInfo.name;
                    bone.position.copy(boneInfo.position);
                    return bone;
                  });
                } else {
                  treeData = this.meshTreeData.addGroup(
                    data.name,
                    [],
                    data.position,
                    data.rotation,
                    data.scale,
                    parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                    false
                  );
                  treeData.id = data.uniqueKey;
                }
                loadChildren(
                  treeData,
                  importData.filter((item) => item.parentId === data.uniqueKey)
                );
              }
            };
            loadChildren(
              null,
              importData.filter((item) => item.parentId === null)
            );

            setTimeout(() => {
              if (this.meshEditor) {
                for (const data of importData) {
                  if (data.ffd) {
                    this.meshEditor.importFfdForMeshId(data.uniqueKey, data.ffd.spanCounts, data.ffd.gridPositionList);
                  }
                  if (data.embossing.length > 0) {
                    this.meshEditor.importEmbossingForMeshId(data.uniqueKey, data.embossing);
                  }
                }
              }
            }, 500);
            this.lastUpdateDB = Date.now();
          }

          /*TemplateService.getCurveData(this.templateId).then((curveImport) => {
            setTimeout(() => {
              if (this.meshEditor) {
                this.meshEditor.importCurves(curveImport);
              }
            }, 500);
          });

          TemplateService.getCameraData(this.templateId).then(
            (cameraImport) => {
              setTimeout(() => {
                if (this.meshEditor) {
                  this.meshEditor.importCameras(cameraImport);
                }
              }, 500);
            }
          );*/
        }

        this.modelLoading = false;
      });
    }
  }

  updateFromDB(): void {
    const getTreeData = (treeItemList: MeshTreeDataItem[], componentId: number | null): MeshTreeDataItem | undefined => {
      const result = treeItemList.find((item) => item.componentId === componentId);
      if (result) return result;
      for (const item of treeItemList) {
        const itemResult = getTreeData(item.children, componentId);
        if (itemResult) return itemResult;
      }
      return undefined;
    };

    if (this.templateId) {
      TemplateService.getMeshData(this.templateId).then((importData) => {
        const selection = this.meshTreeData.getSelection().map((selectedItem) => selectedItem.id);
        this.meshTreeData.selectItem(null);
        if (this.templateId) {
          if (importData && importData.length > 0) {
            const loadChildren = (parent: string | null, children: modelService.MeshExportData[]): void => {
              for (const data of children) {
                let treeData: MeshTreeDataItem | undefined = undefined;
                if (data.fileType !== FileType3D.NONE) {
                  treeData = getTreeData(this.meshTreeData.treeData, data.componentId);
                  //base64 assignment is not enough to update the geometry in editor
                  if (treeData && treeData.base64 !== data.base64) {
                    this.meshTreeData.removeItemTemp(treeData);
                    treeData = this.meshTreeData.addBase64Import(
                      data.uniqueKey,
                      data.componentId,
                      data.name,
                      data.fileType,
                      data.base64,
                      data.thumbnailType,
                      data.thumbnail,
                      parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                      false,
                      data.color,
                      data.position,
                      data.rotation,
                      data.scale
                    );
                  } else if (treeData) {
                    treeData.base64 = data.base64;
                    treeData.position = data.position;
                    treeData.rotation = data.rotation;
                    treeData.scale = data.scale;
                    treeData.color = data.color;
                    this.meshTreeData.setParent(treeData.uuid, parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`));
                  }
                } else {
                  treeData = this.meshTreeData.addGroup(
                    data.name,
                    [],
                    data.position,
                    data.rotation,
                    data.scale,
                    parent ? parent : (this as any).$t(`enum.upload-category.${data.category}`),
                    false
                  );
                  treeData.id = data.uniqueKey;
                }
                if (treeData) {
                  loadChildren(
                    treeData.uuid,
                    importData.filter((item) => item.parentId === treeData?.uuid)
                  );
                }
              }
            };
            loadChildren(
              null,
              importData.filter((item) => item.parentId === null)
            );
            this.meshTreeData.updateStructure();
          }
        }
        if (selection.length > 0 && selection[0]) {
          const selectedItem = this.meshTreeData.getItemById(selection[0]);
          this.meshTreeData.selectItem(selectedItem);
        }
      });
    }
  }

  //#region save
  private async updateTreeItem(categoryType: UploadCategory, item: MeshTreeDataItem, parent: MeshTreeDataItem, lastUpdateDB = -1): Promise<void> {
    if (this.templateId) {
      const awaitTimeout = (delay: number | undefined) => new Promise((resolve) => setTimeout(resolve, delay));
      if (item.lastUpdateDB >= lastUpdateDB) {
        let maxTryCount = 0;
        while (!item.mesh && maxTryCount < 10) {
          maxTryCount++;
          await awaitTimeout(100);
        }
        if (item.mesh) {
          if (item.id) {
            TemplateService.updateMeshData(
              this.templateId,
              item.id,
              categoryType,
              item.name,
              item.mesh,
              item.bones,
              parent.mesh ? parent.componentId : null
            ).then((id) => {
              item.id = id;
            });
          } else {
            TemplateService.addMeshData(this.templateId, categoryType, item.name, item.mesh, item.bones, parent.mesh ? parent.componentId : null).then(
              (result) => {
                item.id = result.id;
              }
            );
          }
          this.updateChildren(categoryType, item, lastUpdateDB);
        }
      }
      if (item.id && this.meshEditor) {
        const ffd = this.meshEditor.exportFfdForMeshId(item.id);
        if (ffd) {
          TemplateService.updateFfdData(this.templateId, item.id, ffd);
        }
        const embossingList = this.meshEditor.exportEmbossingForMeshId(item.id);
        TemplateService.updateEmbossingData(this.templateId, item.id, embossingList);
      }
    }
  }

  private updateChildren(categoryType: UploadCategory, parent: MeshTreeDataItem, lastUpdateDB = -1): void {
    for (const child of parent.children) {
      this.updateTreeItem(categoryType, child, parent, lastUpdateDB);
    }
  }

  private async saveAll(): Promise<void> {
    await this.save();
  }

  private async saveUpdated(): Promise<void> {
    await this.save(this.lastUpdateDB);
  }

  private async save(lastUpdateDB = -1): Promise<void> {
    if (this.templateId) {
      const awaitTimeout = (delay: number | undefined) => new Promise((resolve) => setTimeout(resolve, delay));
      this.fullscreenLoading = true;
      await awaitTimeout(100);
      this.lastUpdateDB = Date.now();
      for (const category of this.meshTreeData.treeData) {
        const categoryType = Object.values(UploadCategory).find((categoryType) => category.name === (this as any).$t(`enum.upload-category.${categoryType}`));
        if (categoryType) {
          this.updateChildren(categoryType, category, lastUpdateDB);
        }
      }
      if (this.meshEditor) {
        const curves = this.meshEditor.exportCurves();
        if (curves) {
          TemplateService.updateCurveData(this.templateId, curves);
        }
        const cameras = this.meshEditor.exportCameras();
        if (cameras) {
          TemplateService.updateCameraData(this.templateId, cameras);
        }
      }
      this.fullscreenLoading = false;
      (this.router as any).askForChanges = this.hasChanges;
    }
  }

  get hasChanges(): boolean {
    return this.lastUpdateDB < this.meshTreeData.lastUpdateDB;
  }

  @Watch('meshTreeData.lastUpdateDB', { immediate: false })
  async onMeshTreeDataDBChanged(): Promise<void> {
    (this.router as any).askForChanges = this.hasChanges;
  }
  //#endregion save

  //#region watch
  @Watch('showTree', { immediate: true })
  onShowTreeChanged(): void {
    LayoutUtility.refresh();
  }

  @Watch('workflowModel.active', { immediate: true })
  onActiveStepChanged(): void {
    if (this.workflowModel.navigationStart && this.hasChanges) this.saveUpdated();
  }

  @Watch('workflowModel.finished', { immediate: true })
  onStepFinished(): void {
    if (this.workflowModel.finished) {
      if (this.hasChanges) {
        this.saveUpdated().then(() => {
          this.router.back();
        });
      } else {
        this.router.back();
      }
    }
  }

  @Watch('templateId', { immediate: true })
  onTemplateIdChanged(): void {
    this.meshTreeData.modelId = this.templateId;
    this.meshTreeData.historyList.modelId = this.templateId;
    this.meshTreeData.historyList.defaultUndoRedo = this;
  }

  //#endregion watch

  deleteItem(item: MeshTreeDataItem): void {
    if (this.templateId && item.id) TemplateService.deleteMeshData(this.templateId, item.id);
  }

  async defaultUndo(): Promise<void> {
    if (this.templateId) {
      await modelService.undo(this.templateId, 1).then(() => {
        //todo: geometry is updated in the backend with a delay. 500 milliseconds are not enough
        setTimeout(() => {
          this.updateFromDB();
        }, 500);
      });
    }
  }

  async defaultRedo(): Promise<void> {
    if (this.templateId) {
      await modelService.redo(this.templateId, 1).then(() => {
        //todo: geometry is updated in the backend with a delay. 500 milliseconds are not enough
        setTimeout(() => {
          this.updateFromDB();
        }, 500);
      });
    }
  }

  productQualityChanged(selectedProductQuality: string): void {
    this.selectedProductQuality = selectedProductQuality;
    this.loadFromDB();
  }

  handleBack(): void {
    this.router.push('/templates');
  }
}
</script>

<style lang="scss" scoped>
.el-main :deep() {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}

.level:not(:last-child) {
  margin-bottom: unset;
}

.screenHeight {
  display: flex;
  flex-direction: column;
  height: 93vh;
}

.minimize {
  width: 0;
}

.el-button.is-link {
  color: white;
}

.el-button.is-link:hover,
.el-button.is-link:focus {
  color: white;
}

.level:not(:last-child) {
  margin-bottom: unset;
}

.level,
.workflow,
.el-aside {
  z-index: 1;
}

.el-footer {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.el-steps) {
  width: 100%;
}
</style>
